import { Suspense, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { DateTime } from "luxon"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../core/api/errorTypes"
import { MERCHANT_ADMIN_ROUTE, MERCHANT_NEWS_ROUTE } from "../../../../../constants/routes"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { FetchArticleResponse, MerchantAdminNewsQuery, fetchArticle, updateArticle } from "../merchantAdminNewsApi"
import { CreateOrEditArticleForm } from "../components/CreateOrEditArticleForm"
import { queryKeyFor, useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { CreateOrEditArticleFormValues, MerchantArticleStatus } from "../merchantNewsItemType"
import { FormikProps } from "formik"
import { CreateAndPublishSplitButton } from "../components/CreateAndPublishSplitButton"

const selectArticle = (data: FetchArticleResponse) => {
    const article = data.news
    if (!article) return null
    return {
        ...article,
        ...(article?.publishDate ? { publishDate: DateTime.fromISO(article.publishDate) } : { publishDate: undefined }),
        ...(article?.expirationDate
            ? { expirationDate: DateTime.fromISO(article.expirationDate) }
            : { expirationDate: undefined }),
    }
}

const Article = () => {
    const { articleId } = useParams()
    const { merchantId } = useMerchant()
    const navigate = useNavigate()

    const articleResult = useApiQuery(fetchArticle, {
        queryName: MerchantAdminNewsQuery.ARTICLE_DETAILS,
        dependencies: {
            articleId: articleId ?? "",
        },
        enabled: Boolean(articleId),
        select: selectArticle,
        suspense: true,
        useErrorBoundary: true,
    })

    const formRef = useRef<FormikProps<CreateOrEditArticleFormValues>>(null)
    const [status, setStatus] = useState<MerchantArticleStatus>(
        articleResult.data?.status || MerchantArticleStatus.DRAFT,
    )

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    const { mutateAsync: callUpdateArticle } = useApiMutation(updateArticle, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, _, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MerchantAdminNewsQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(MerchantAdminNewsQuery.ARTICLE_DETAILS, { articleId: params.articleId }),
            })
            navigate(MERCHANT_NEWS_ROUTE)
        },
    })

    return (
        <>
            <LocalizedStrict id="edit-news-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_NEWS_EDIT}
                    title="Edit Article"
                    rightElement={
                        <CreateAndPublishSplitButton
                            status={articleResult.data?.status || MerchantArticleStatus.DRAFT}
                            setStatus={setStatus}
                            handleClick={handleSaveClick}
                        />
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-communication-header">
                            <Breadcrumb>Communication</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-merchant-news">
                            <BreadcrumbLink to={MERCHANT_NEWS_ROUTE}>News</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="nav-item-edit-news-header">
                            <Breadcrumb>Edit Article</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <CreateOrEditArticleForm
                ref={formRef}
                onSubmit={(data) =>
                    articleId && callUpdateArticle({ article: { ...data, status }, merchantId, articleId: articleId })
                }
                initialValues={articleResult.data}
            />
        </>
    )
}

export function UpdateNews() {
    return (
        <QueryErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
                <Article />
            </Suspense>
        </QueryErrorBoundary>
    )
}
